// Tabs/AdditionalDetailsTab.tsx

import { useCallback, useEffect, useState } from 'react';
import {
  Table, Text, ActionIcon, Group, Stack, Center, Loader, Alert,
} from '@mantine/core';
import { IconAlertCircle, IconPhoto, IconRefresh } from '@tabler/icons-react';
import {
  useRegistrationService,
  RegistrationCourseCustomDataModelView,
} from '@actschurch/ccms-sdk';
import { UniversalFileViewer } from '@/components/ccms-components/file-management/components/UniversalFileViewer';

export interface AdditionalDetailsTabProps {
  registrationId: number;
  active: boolean;
}

function formatValue(row: RegistrationCourseCustomDataModelView): string {
  if (row.valueBoolean !== null && row.valueBoolean !== undefined) return row.valueBoolean ? 'Yes' : 'No';
  if (row.valueDecimal !== null && row.valueDecimal !== undefined) return String(row.valueDecimal);
  if (row.valueNumber !== null && row.valueNumber !== undefined) return String(row.valueNumber);
  if (row.valueDateTime !== null && row.valueDateTime !== undefined) return String(row.valueDateTime);
  return row.valueText ?? row.value ?? '—';
}

/** 
 * Returns true if the answer is a valid image URL.
 * Heavily robust to handle URL spaces, encoding, and trailing query parameters.
 */
function isImageAnswer(row: RegistrationCourseCustomDataModelView): boolean {
  const value = formatValue(row);
  if (!value || value === '—') return false;

  const lowerValue = value.toLowerCase();

  // 1. Must be a URL structure
  const isUrl = lowerValue.startsWith('http://') || lowerValue.startsWith('https://');
  if (!isUrl) return false;

  // 2. Explicitly configured as URL field
  if (row.field?.fieldTypeCode === 'url') return true;

  // 3. Robust check: Does the URL contain an image extension (even with spaces or query strings)?
  try {
    const decodedValue = decodeURIComponent(lowerValue);
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(decodedValue);
    if (hasImageExtension) return true;
  } catch (e) {
    const hasImageExtension = /\.(jpeg|jpg|gif|png|webp|svg|bmp)/i.test(lowerValue);
    if (hasImageExtension) return true;
  }

  // 4. Hardcoded safety net for AWS S3 temp uploads
  if (lowerValue.includes('s3.af-south-1.amazonaws.com/temp/uploads')) {
    return true;
  }

  return false;
}

export function AdditionalDetailsTab({ registrationId, active }: AdditionalDetailsTabProps) {
  const registrationService = useRegistrationService();

  const [rows, setRows] = useState<RegistrationCourseCustomDataModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Image viewer state ──────────────────────────────────────────────────
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState<RegistrationCourseCustomDataModelView | null>(null);

  const load = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await (registrationService as any).getCourseCustomData(registrationId);
      const data: RegistrationCourseCustomDataModelView[] = Array.isArray(res) ? res : res?.data ?? [];
      setRows(data.filter((r) => r.field?.fieldKey !== 'children'));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load answers.');
    } finally {
      setLoading(false);
    }
  }, [registrationService, registrationId]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const openViewer = (row: RegistrationCourseCustomDataModelView) => {
    setViewerRow(row);
    setViewerOpen(true);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const viewerFiles = viewerRow
    ? [
        {
          id: String(viewerRow.registrationCourseCustomDataID),
          url: formatValue(viewerRow),
          fileName: viewerRow.field?.label ?? 'Image',
          mimeType: 'image/*',
          fileSize: 0,
        },
      ]
    : [];

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Group justify="flex-end">
        <ActionIcon
          variant="subtle"
          aria-label="Refresh additional details"
          onClick={load}
          loading={loading}
          size="lg"
        >
          <IconRefresh size={16} />
        </ActionIcon>
      </Group>

      {rows.length === 0 ? (
        <Text c="dimmed" size="sm">No answers captured yet.</Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Question</Table.Th>
              <Table.Th>Answer</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.registrationCourseCustomDataID}>
                <Table.Td>{row.field?.label ?? '—'}</Table.Td>
                <Table.Td>
                  {isImageAnswer(row) ? (
                    <Group
                      gap={4}
                      style={{ cursor: 'pointer', display: 'inline-flex' }}
                      onClick={() => openViewer(row)}
                    >
                      <IconPhoto size={14} color="var(--mantine-color-blue-filled)" />
                      <Text size="sm" c="blue" td="underline">
                        View image
                      </Text>
                    </Group>
                  ) : (
                    formatValue(row)
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <UniversalFileViewer
        opened={viewerOpen}
        onClose={() => {
          setViewerOpen(false);
          setViewerRow(null);
        }}
        files={viewerFiles}
        initialIndex={0}
        showGallery={false}
      />
    </Stack>
  );
}

export default AdditionalDetailsTab;